// ── Keyword chip selector ─────────────────────────────────────────
// 跨 step 共享的关键词 chip 渲染 + 事件绑定。
//
// 历史背景：step02 的 renderStCandidates、step06 的 renderSearchTermBubbles /
// renderSearchTermSuggestions 三处独立实现，都用同一段 .st-candidate-tag 模板。
// 该 helper 收口并支持各 step 的扩展点：
//   - showSearchLink：Amazon 站内搜索 ↗ 链接（step02 用）
//   - sourceClassResolver：根据 item.source 决定额外 class（step02 区分 library / market-data）
//   - translateMap：显示翻译文本（step02 中文理解模式）
//   - onToggle：checkbox 变化回调，由调用方决定 push/remove 到自家 state

import { formatVolume } from './utils.js';

const AMAZON_DOMAINS = {
  US: 'amazon.com', DE: 'amazon.de', FR: 'amazon.fr',
  IT: 'amazon.it', ES: 'amazon.es', CA: 'amazon.ca',
  AU: 'amazon.com.au', UK: 'amazon.co.uk', JP: 'amazon.co.jp',
};

/**
 * 根据当前页面 marketplace 选择器返回 Amazon 搜索 URL。
 * 若页面没有 #product-marketplace（如 step6 编辑器），fallback 到 US。
 */
export function amazonSearchUrl(keyword, marketplace) {
  const mp = String(
    marketplace
    || (typeof document !== 'undefined' && document.querySelector('#product-marketplace')?.value)
    || 'US'
  ).toUpperCase();
  const domain = AMAZON_DOMAINS[mp] || AMAZON_DOMAINS.US;
  return `https://${domain}/s?k=${encodeURIComponent(keyword || '')}`;
}

/**
 * @typedef {Object} ChipItem
 * @property {string} keyword
 * @property {number} [searches30d]
 * @property {string} [source]   - 'library' | 'market-data' | ...
 */

/**
 * @typedef {Object} ChipOptions
 * @property {Set<string>|((kw:string)=>boolean)} [selectedSet]
 *   决定 chip 是否带 .selected。Set 用 keyword 原文匹配；函数版接收 keyword 返回 boolean。
 * @property {(keyword:string, checked:boolean, item:ChipItem) => void} [onToggle]
 *   checkbox 变化回调
 * @property {boolean} [showSearchLink=false]    显示 Amazon ↗ 搜索链接
 * @property {boolean} [showVolume=true]         显示搜索量
 * @property {Map<string,string>} [translateMap] keyword(lower) → 翻译文本
 * @property {boolean} [translatedView=false]    当前是否中文理解模式（控制 .translated-view class）
 * @property {(item:ChipItem) => string} [sourceClassResolver]
 *   根据 item 返回额外 class（如 'market-data-kw mcp-kw'）
 * @property {string} [marketplace] - Amazon 域名标识；不传则从 #product-marketplace 读
 */

/**
 * 渲染一组 chip 到 container，并绑定 checkbox 事件。
 *
 * 调用者负责：维护选中集合、调 onToggle 时更新自己的 state、必要时重渲。
 * 本 helper 只负责：DOM 模板 + 标准 checkbox event hookup。
 *
 * @param {HTMLElement|null} container
 * @param {ChipItem[]} items
 * @param {ChipOptions} [options]
 */
export function renderKeywordChips(container, items, options = {}) {
  if (!container) return;
  const {
    selectedSet,
    onToggle,
    showSearchLink = false,
    showVolume = true,
    translateMap,
    translatedView = false,
    sourceClassResolver,
    marketplace,
  } = options;

  const isSelected = typeof selectedSet === 'function'
    ? selectedSet
    : (kw) => (selectedSet instanceof Set ? selectedSet.has(kw) : false);

  if (container._keywordChipChangeHandler) {
    container.removeEventListener('change', container._keywordChipChangeHandler);
    delete container._keywordChipChangeHandler;
  }

  const chips = [];
  const itemsByKeyword = new Map();
  for (const item of items || []) {
    const kw = String(item?.keyword || '');
    if (!kw) continue;
    if (!itemsByKeyword.has(kw)) itemsByKeyword.set(kw, item);
    const selected = Boolean(isSelected(kw, item));
    const sourceClass = sourceClassResolver ? sourceClassResolver(item) || '' : '';
    const display = translatedView && translateMap?.get(kw.toLowerCase())
      ? translateMap.get(kw.toLowerCase())
      : kw;
    const label = document.createElement('label');
    label.classList.add('st-candidate-tag');
    if (selected) label.classList.add('selected');
    if (translatedView) label.classList.add('translated-view');
    for (const className of String(sourceClass).split(/\s+/).filter(Boolean)) {
      label.classList.add(className);
    }
    label.dataset.keyword = kw;

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = selected;
    checkbox.dataset.keyword = kw;
    label.append(checkbox);

    const text = document.createElement('span');
    text.className = 'st-candidate-text';
    text.textContent = String(display);
    label.append(text);

    if (showVolume) {
      const volume = document.createElement('span');
      volume.className = 'kw-volume';
      volume.textContent = formatVolume(item?.searches30d);
      label.append(volume);
    }

    if (showSearchLink) {
      const searchLink = document.createElement('a');
      searchLink.className = 'kw-amazon-search';
      searchLink.href = amazonSearchUrl(kw, marketplace);
      searchLink.target = '_blank';
      searchLink.rel = 'noopener';
      searchLink.title = '在 Amazon 搜索此关键词';
      searchLink.textContent = '↗';
      searchLink.addEventListener('click', (event) => event.stopPropagation());
      label.append(searchLink);
    }
    chips.push(label);
  }
  container.replaceChildren(...chips);

  if (typeof onToggle === 'function') {
    const handleCheckboxChange = (event) => {
      const checkbox = event.target;
      if (!checkbox?.matches?.('input[type="checkbox"][data-keyword]')) return;
      const kw = checkbox.dataset.keyword || '';
      onToggle(kw, checkbox.checked, itemsByKeyword.get(kw) || { keyword: kw });
    };
    container._keywordChipChangeHandler = handleCheckboxChange;
    container.addEventListener('change', handleCheckboxChange);
  }
}
